import { memo } from "react";
import { Box, Container, Typography, Grid2, Card, CardContent } from "@mui/material";
import { FullPaddingWrapper } from "../../Components/FullPaddingWrapper/FullPaddingWrapper";
import {
  AttachMoney,
  Home,
  Build,
  Security,
  WaterDrop,
  Bolt,
  Grass,
  AcUnit,
  Roofing,
  Foundation,
  Plumbing,
  LocalFireDepartment,
} from "@mui/icons-material";

// Cost categories with icons and descriptions
const ANNUAL_COSTS = [
  {
    icon: Security,
    title: "Homeowners Insurance",
    range: "$1,200 - $3,500/year",
    description:
      "Protects your home and belongings against damage, theft, and liability. Costs vary by location, home value, and coverage level.",
  },
  {
    icon: AttachMoney,
    title: "Property Taxes",
    range: "0.5% - 2.5% of home value",
    description:
      "Annual taxes based on your home's assessed value. Rates vary significantly by state and municipality.",
  },
  {
    icon: Bolt,
    title: "Electricity",
    range: "$100 - $300/month",
    description:
      "Monthly electricity costs depend on home size, climate, and energy efficiency. Older homes typically cost more.",
  },
  {
    icon: LocalFireDepartment,
    title: "Gas/Heating",
    range: "$50 - $200/month",
    description:
      "Natural gas or heating oil costs for heating and cooking. Costs peak during winter months in cold climates.",
  },
  {
    icon: WaterDrop,
    title: "Water & Sewer",
    range: "$30 - $100/month",
    description:
      "Municipal water and sewer services. Costs vary by usage and local utility rates.",
  },
  {
    icon: Grass,
    title: "Lawn & Landscaping",
    range: "$100 - $400/month",
    description:
      "Professional lawn care, fertilization, and seasonal landscaping maintenance.",
  },
];

const MAINTENANCE_COSTS = [
  {
    icon: AcUnit,
    title: "HVAC Maintenance",
    frequency: "Annually",
    cost: "$150 - $300/year",
    description:
      "Regular tune-ups, filter replacements, and inspections keep your heating and cooling system efficient.",
  },
  {
    icon: Roofing,
    title: "Roof Inspection",
    frequency: "Every 2-3 years",
    cost: "$150 - $400",
    description:
      "Professional inspections can catch small issues before they become expensive repairs.",
  },
  {
    icon: Plumbing,
    title: "Plumbing Maintenance",
    frequency: "As needed",
    cost: "$150 - $500/year",
    description:
      "Drain cleaning, water heater flushing, and fixing minor leaks prevent major problems.",
  },
  {
    icon: Foundation,
    title: "Foundation Check",
    frequency: "Every 3-5 years",
    cost: "$300 - $500",
    description:
      "Early detection of foundation issues can save tens of thousands in repair costs.",
  },
];

const MAJOR_REPLACEMENTS = [
  {
    item: "Roof Replacement",
    lifespan: "20-30 years",
    cost: "$8,000 - $25,000",
    note: "Varies by material (asphalt, metal, tile)",
  },
  {
    item: "HVAC System",
    lifespan: "15-20 years",
    cost: "$5,000 - $15,000",
    note: "Includes furnace and AC unit",
  },
  {
    item: "Water Heater",
    lifespan: "10-15 years",
    cost: "$1,000 - $3,500",
    note: "Tankless units cost more but last longer",
  },
  {
    item: "Windows",
    lifespan: "20-25 years",
    cost: "$300 - $1,000 each",
    note: "Energy-efficient windows save on utilities",
  },
  {
    item: "Exterior Paint",
    lifespan: "5-10 years",
    cost: "$3,000 - $8,000",
    note: "Climate affects paint durability",
  },
  {
    item: "Appliances",
    lifespan: "10-15 years",
    cost: "$500 - $2,500 each",
    note: "Refrigerator, washer, dryer, dishwasher",
  },
];

const TrueCostGuide = memo(() => {
  return (
    <Container maxWidth="xl" disableGutters>
      <FullPaddingWrapper>
        {/* Hero Section */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #1565C0 0%, #1976D2 100%)",
            borderRadius: { xs: "16px", md: "24px" },
            p: { xs: 4, sm: 6, md: 8 },
            mb: { xs: 4, md: 6 },
            mt: 4,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
            <Home
              sx={{ fontSize: { xs: 32, md: 48 }, color: "#ffffff", mr: 2 }}
            />
            <Typography
              variant="h2"
              sx={{
                color: "#ffffff",
                fontWeight: 700,
                fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
                lineHeight: 1.2,
              }}
            >
              True Cost Guide
            </Typography>
          </Box>
          <Typography
            variant="body1"
            sx={{
              color: "#ffffff",
              fontSize: { xs: "1rem", md: "1.1rem" },
              opacity: 0.9,
              maxWidth: 800,
            }}
          >
            Understanding the real costs of homeownership helps you budget
            effectively and avoid financial surprises. This guide breaks down
            the recurring expenses, maintenance costs, and major replacements
            every homeowner should plan for.
          </Typography>
        </Box>

        {/* Introduction */}
        <Box
          sx={{
            px: { xs: 2, md: 6, lg: 10 },
            py: { xs: 2, md: 4 },
            maxWidth: 1200,
            mx: "auto",
          }}
        >
          <Box
            sx={{
              p: 4,
              borderRadius: "16px",
              backgroundColor: "#fff8e1",
              border: "1px solid #ffcc02",
              mb: 6,
            }}
          >
            <Typography
              variant="h5"
              sx={{ color: "#f57c00", fontWeight: 600, mb: 2 }}
            >
              The 1% Rule
            </Typography>
            <Typography variant="body1" sx={{ color: "#555", lineHeight: 1.8 }}>
              A common guideline is to budget <strong>1-2% of your home's value
              annually</strong> for maintenance and repairs. For a $400,000
              home, that's $4,000-$8,000 per year. While actual costs vary,
              this provides a baseline for financial planning.
            </Typography>
          </Box>

          {/* Annual Recurring Costs */}
          <Box sx={{ mb: 8 }}>
            <Typography
              variant="h4"
              sx={{
                color: "primary.main",
                fontWeight: 700,
                fontSize: { xs: "1.5rem", md: "1.75rem" },
                mb: 4,
              }}
            >
              Annual Recurring Costs
            </Typography>
            <Grid2 container spacing={3}>
              {ANNUAL_COSTS.map((cost, index) => (
                <Grid2 key={index} size={{ xs: 12, sm: 6, md: 4 }}>
                  <Card
                    elevation={0}
                    sx={{
                      height: "100%",
                      border: "1px solid #e0e0e0",
                      borderRadius: "12px",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        borderColor: "primary.main",
                        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                      },
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                        <cost.icon sx={{ color: "primary.main", mr: 1.5 }} />
                        <Typography variant="h6" fontWeight={600}>
                          {cost.title}
                        </Typography>
                      </Box>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          color: "#1565C0",
                          fontWeight: 600,
                          mb: 1.5,
                        }}
                      >
                        {cost.range}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "#666", lineHeight: 1.7 }}
                      >
                        {cost.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid2>
              ))}
            </Grid2>
          </Box>

          {/* Regular Maintenance */}
          <Box sx={{ mb: 8 }}>
            <Typography
              variant="h4"
              sx={{
                color: "primary.main",
                fontWeight: 700,
                fontSize: { xs: "1.5rem", md: "1.75rem" },
                mb: 4,
              }}
            >
              Regular Maintenance
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: "#555", lineHeight: 1.8, mb: 4 }}
            >
              Preventive maintenance extends the life of your home's systems
              and prevents costly emergency repairs.
            </Typography>
            <Grid2 container spacing={3}>
              {MAINTENANCE_COSTS.map((item, index) => (
                <Grid2 key={index} size={{ xs: 12, sm: 6 }}>
                  <Box
                    sx={{
                      p: 3,
                      borderRadius: "12px",
                      backgroundColor: "#f5f5f5",
                      height: "100%",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                      <Box
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: "50%",
                          backgroundColor: "primary.main",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mr: 2,
                        }}
                      >
                        <item.icon sx={{ color: "#ffffff", fontSize: 24 }} />
                      </Box>
                      <Box>
                        <Typography variant="h6" fontWeight={600}>
                          {item.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {item.frequency}
                        </Typography>
                      </Box>
                    </Box>
                    <Typography
                      variant="subtitle1"
                      sx={{ color: "#1565C0", fontWeight: 600, mb: 1 }}
                    >
                      {item.cost}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#666" }}>
                      {item.description}
                    </Typography>
                  </Box>
                </Grid2>
              ))}
            </Grid2>
          </Box>

          {/* Major Replacements */}
          <Box sx={{ mb: 8 }}>
            <Typography
              variant="h4"
              sx={{
                color: "primary.main",
                fontWeight: 700,
                fontSize: { xs: "1.5rem", md: "1.75rem" },
                mb: 4,
              }}
            >
              Major Replacements to Plan For
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: "#555", lineHeight: 1.8, mb: 4 }}
            >
              Every major home component has a finite lifespan. Planning ahead
              helps you avoid financial strain when replacements are needed.
            </Typography>
            <Box
              sx={{
                borderRadius: "12px",
                border: "1px solid #e0e0e0",
                overflow: "hidden",
              }}
            >
              {/* Table Header */}
              <Box
                sx={{
                  display: { xs: "none", md: "grid" },
                  gridTemplateColumns: "1fr 1fr 1fr 1.5fr",
                  backgroundColor: "primary.main",
                  p: 2,
                }}
              >
                <Typography sx={{ color: "#fff", fontWeight: 600 }}>
                  Item
                </Typography>
                <Typography sx={{ color: "#fff", fontWeight: 600 }}>
                  Lifespan
                </Typography>
                <Typography sx={{ color: "#fff", fontWeight: 600 }}>
                  Cost Range
                </Typography>
                <Typography sx={{ color: "#fff", fontWeight: 600 }}>
                  Notes
                </Typography>
              </Box>
              {/* Table Rows */}
              {MAJOR_REPLACEMENTS.map((item, index) => (
                <Box
                  key={index}
                  sx={{
                    display: { xs: "block", md: "grid" },
                    gridTemplateColumns: "1fr 1fr 1fr 1.5fr",
                    p: { xs: 3, md: 2 },
                    borderBottom:
                      index < MAJOR_REPLACEMENTS.length - 1
                        ? "1px solid #e0e0e0"
                        : "none",
                    backgroundColor: index % 2 === 0 ? "#fff" : "#fafafa",
                  }}
                >
                  <Box sx={{ mb: { xs: 1, md: 0 } }}>
                    <Typography
                      variant="caption"
                      sx={{
                        display: { xs: "block", md: "none" },
                        color: "#888",
                      }}
                    >
                      Item
                    </Typography>
                    <Typography fontWeight={600}>{item.item}</Typography>
                  </Box>
                  <Box sx={{ mb: { xs: 1, md: 0 } }}>
                    <Typography
                      variant="caption"
                      sx={{
                        display: { xs: "block", md: "none" },
                        color: "#888",
                      }}
                    >
                      Lifespan
                    </Typography>
                    <Typography>{item.lifespan}</Typography>
                  </Box>
                  <Box sx={{ mb: { xs: 1, md: 0 } }}>
                    <Typography
                      variant="caption"
                      sx={{
                        display: { xs: "block", md: "none" },
                        color: "#888",
                      }}
                    >
                      Cost Range
                    </Typography>
                    <Typography sx={{ color: "#1565C0", fontWeight: 500 }}>
                      {item.cost}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        display: { xs: "block", md: "none" },
                        color: "#888",
                      }}
                    >
                      Notes
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#666" }}>
                      {item.note}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Tips Section */}
          <Box
            sx={{
              p: 4,
              borderRadius: "16px",
              backgroundColor: "#e8f5e9",
              border: "1px solid #81c784",
            }}
          >
            <Typography
              variant="h5"
              sx={{ color: "#2e7d32", fontWeight: 600, mb: 3 }}
            >
              <Build sx={{ mr: 1, verticalAlign: "middle" }} />
              Money-Saving Tips
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 3 }}>
              <Typography
                component="li"
                variant="body1"
                sx={{ color: "#555", lineHeight: 2 }}
              >
                <strong>Create an emergency fund</strong> with 3-6 months of
                home expenses for unexpected repairs.
              </Typography>
              <Typography
                component="li"
                variant="body1"
                sx={{ color: "#555", lineHeight: 2 }}
              >
                <strong>Get multiple quotes</strong> for major work - prices can
                vary 30-50% between contractors.
              </Typography>
              <Typography
                component="li"
                variant="body1"
                sx={{ color: "#555", lineHeight: 2 }}
              >
                <strong>Schedule off-season work</strong> - HVAC service in
                spring/fall and roofing in late fall often cost less.
              </Typography>
              <Typography
                component="li"
                variant="body1"
                sx={{ color: "#555", lineHeight: 2 }}
              >
                <strong>Invest in energy efficiency</strong> - LED bulbs, smart
                thermostats, and insulation pay for themselves.
              </Typography>
              <Typography
                component="li"
                variant="body1"
                sx={{ color: "#555", lineHeight: 2 }}
              >
                <strong>Learn basic DIY</strong> - Simple tasks like caulking,
                painting, and filter changes save hundreds yearly.
              </Typography>
            </Box>
          </Box>
        </Box>
      </FullPaddingWrapper>
    </Container>
  );
});

export default TrueCostGuide;
